import { FC, useMemo, lazy, Suspense } from "react";
const VerificationTemplate = lazy(() => import("../"));

import { useVerificationTemplateController } from "views/verification-templates-id/controller";
import { Loader } from "shared/components/Loader";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import {
  Template,
  VerificationSchema
} from "types/landlord/verificationTemplate";
import {
  ApplicantType,
  ApplicationStatus,
  VerificationNames
} from "shared/types";
import { LayoutHeader } from "../components/LayoutHeader";
import { SideBar } from "../components/SideBar";
import { LayoutFooter } from "../components/LayoutFooter";
import { onMountTemplatePreparations } from "../helpers";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

type Props = {
  opened: boolean;
  onClose: () => void;
  id?: string;
  template?: Template;
  applicationId?: string;
  applicationStatus?: ApplicationStatus;
  propertyId?: number;
};

interface ModalContentProps {
  isLoading: boolean;
  template: Template | undefined;
  defaultTemplate: Template;
  verificationSchemas: VerificationSchema[];
  onClose: () => void;
  applicationId?: string;
  applicationStatus?: ApplicationStatus;
  propertyId?: number;
}

const ModalContent: FC<ModalContentProps> = ({
  isLoading,
  template,
  defaultTemplate,
  verificationSchemas,
  onClose,
  applicationId,
  applicationStatus,
  propertyId
}) => {
  if (isLoading) return <Loader />;
  if (!template)
    return (
      <>
        <div className="flex justify-end p-6">
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
          />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <h1 className="text-lg font-bold">Template not found</h1>
        </div>
      </>
    );
  return (
    <Suspense fallback={<Loader />}>
      <VerificationTemplate
        layoutProps={{
          header: (
            <>
              <div className="flex p-4 border-b border-alpha-40 justify-end bg-background-1000">
                <IconButton
                  variant={IconButtonVariant.TEXT_BASE}
                  size={IconButtonSize.SM}
                  onClick={onClose}
                  HugeIcon={Cancel01Icon}
                />
              </div>
              <LayoutHeader />
            </>
          ),
          sidebar: (
            <SideBar
              isModal
              template={template}
              editedName={template.name}
              applicationId={applicationId}
              applicationStatus={applicationStatus}
              propertyId={propertyId}
            />
          ),
          footer: <LayoutFooter onCancel={onClose} />
        }}
        verificationTemplate={template}
        savedTemplate={defaultTemplate}
        verificationSchemas={verificationSchemas || []}
        readOnly
        isModal
        originalQualificationsDisclosureText={
          template.verification_schemas[ApplicantType.Applicant][
            VerificationNames.TermsAndConditions
          ]?.additional_text || ""
        }
        originalSubmissionDisclosureText={
          template.verification_schemas[ApplicantType.Applicant][
            VerificationNames.SubmissionDisclosure
          ]?.additional_text || ""
        }
      />
    </Suspense>
  );
};

export const TemplateReviewModal: FC<Props> = ({
  opened,
  onClose,
  id,
  template: propTemplate,
  applicationId,
  applicationStatus,
  propertyId
}) => {
  const { verificationSchemas, templateQuery, defaultTemplate } =
    useVerificationTemplateController(opened && !propTemplate ? id : undefined);

  const template = useMemo(() => {
    const sourceTemplate =
      propTemplate || templateQuery.data?.verification_template;

    if (!sourceTemplate) return undefined;

    const currentVerificationTemplate = sourceTemplate;

    if (defaultTemplate)
      currentVerificationTemplate.verification_schemas =
        onMountTemplatePreparations.populateDefaultSchemas(
          defaultTemplate,
          currentVerificationTemplate
        );

    currentVerificationTemplate.settings = {
      ...currentVerificationTemplate.settings,
      applicant: {
        ...currentVerificationTemplate.settings.applicant,
        enabled_screening_services: true
      },
      co_signer: {
        ...currentVerificationTemplate.settings.co_signer,
        enabled_screening_services:
          currentVerificationTemplate.settings.co_signer
            .enabled_screening_services ?? true
      }
    };

    return currentVerificationTemplate;
  }, [
    propTemplate,
    defaultTemplate,
    templateQuery.data?.verification_template
  ]);

  if (!defaultTemplate) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="mx-auto h-[90dvh] w-[90dvw] overflow-y-auto overscroll-none bg-background-900 rounded-lg overflow-hidden"
    >
      <ModalContent
        isLoading={templateQuery.isLoading}
        template={template}
        defaultTemplate={defaultTemplate}
        verificationSchemas={verificationSchemas || []}
        onClose={onClose}
        applicationId={applicationId}
        applicationStatus={applicationStatus}
        propertyId={propertyId}
      />
    </Modal>
  );
};
